// ============================================================================
// REGISTRATION TYPE SERVICE
// ============================================================================
// Business logic and HTTP calls for the RegistrationType master
// (basic.tbl_registration_type via /api/v1/RegistrationTypes).
//
// Backend wraps every response in ApiResponse<T>; list endpoints additionally
// wrap data in PagedResult<T>. `getAll()` transforms PagedResult<T> into the
// PaginatedResponse<T> that SyutGrid consumes — see CLAUDE.md
// "Service layer transformation".

import { get, post, put, del } from '../client/axiosClient';
import { registrationTypeEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  RegistrationType,
  CreateRegistrationTypeRequest,
  UpdateRegistrationTypeRequest,
  RegistrationTypeSearchRequest,
} from '../types';

class RegistrationTypeService {
  /**
   * Get paginated list with optional filters.
   * Transforms backend PagedResult<T> → frontend PaginatedResponse<T>.
   */
  async getAll(
    params?: RegistrationTypeSearchRequest
  ): Promise<PaginatedResponse<RegistrationType>> {
    // Use raw BASE_PATH (NOT registrationTypeEndpoints.list()) —
    // buildEndpointWithQuery adds the /v1 prefix internally.
    const endpoint = params
      ? buildEndpointWithQuery(registrationTypeEndpoints.basePath(), params as Record<string, any>)
      : registrationTypeEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data; // ApiResponse<PagedResult<RegistrationType>>
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<RegistrationType>> {
    const response = await get<ApiResponse<RegistrationType>>(
      registrationTypeEndpoints.getById(id)
    );
    return response.data;
  }

  async create(
    data: CreateRegistrationTypeRequest
  ): Promise<ApiResponse<RegistrationType>> {
    const response = await post<ApiResponse<RegistrationType>>(
      registrationTypeEndpoints.create(),
      data
    );
    return response.data;
  }

  async update(
    id: ID,
    data: UpdateRegistrationTypeRequest
  ): Promise<ApiResponse<RegistrationType>> {
    const response = await put<ApiResponse<RegistrationType>>(
      registrationTypeEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * DELETE returns 204 No Content on success, 404 when absent, and 409 when
   * the type is still referenced by client registrations.
   */
  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(registrationTypeEndpoints.delete(id));
    return { success: true };
  }
}

export const registrationTypeService = new RegistrationTypeService();
export default registrationTypeService;
